// Behaviour for the public site.
//
// Two deliberate rules here:
//
// 1. Whether content is VISIBLE never depends on JavaScript. The reveal cadence
//    is pure CSS (.cc-reveal hidden, .is-in shown); this file only adds .is-in
//    as things scroll into view. If the bundle is slow, blocked, or the visitor
//    is offline, the page still reads. The head script drops .cc-motion when
//    motion is reduced or when this file never signals ready.
// 2. The day arc's line, the suns it lights and the meadow under it (grass
//    that rises, then flowers that open) are the one other animation this file
//    drives, and it is decoration: two scroll positions mapped onto a dash
//    offset, a custom property and a class per flower, no library. If this
//    file never runs, the CSS resting state shows the day done instead. The
//    smooth scroll is decoration in the same sense: without it the page
//    scrolls as the browser scrolls.
//
// flatpickr and Lenis are the two npm packages this bundle pulls in. flatpickr
// is here rather than in a Stimulus controller because this layout deliberately
// ships no Stimulus, and DESIGN.md's open-gaps note already prescribes this
// route: port the behaviour into this file rather than pull the dashboard
// bundle onto a brochure site.

import Lenis from "lenis";
import { attachDatePicker } from "./lib/date_picker";
import { formatPhone, maskPhoneInput, phoneValidityMessage } from "./lib/phone_mask";

let cleanup = null;

// The menu closes the three ways a visitor expects a panel to close: its own
// button, the Escape key, and a tap anywhere outside it. Escape hands focus back
// to the button, so a keyboard visitor is not left on a link that just vanished;
// a tap outside leaves focus where the tap put it.
const mobileMenu = () => {
  const button = document.querySelector("[data-cc-menu-button]");
  const nav = document.querySelector("[data-cc-mobile-nav]");
  if (!button || !nav) return () => {};

  const isOpen = () => !nav.classList.contains("hidden");

  const setOpen = (open) => {
    nav.classList.toggle("hidden", !open);
    button.setAttribute("aria-expanded", String(open));
    button.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  };

  const onClick = () => setOpen(!isOpen());

  const onKeydown = (event) => {
    if (event.key !== "Escape" || !isOpen()) return;
    setOpen(false);
    button.focus();
  };

  const onDocumentClick = (event) => {
    if (!isOpen() || nav.contains(event.target) || button.contains(event.target)) return;
    setOpen(false);
  };

  button.addEventListener("click", onClick);
  document.addEventListener("keydown", onKeydown);
  document.addEventListener("click", onDocumentClick);
  return () => {
    button.removeEventListener("click", onClick);
    document.removeEventListener("keydown", onKeydown);
    document.removeEventListener("click", onDocumentClick);
  };
};

const reveals = () => {
  const root = document.documentElement;
  const elements = Array.from(document.querySelectorAll(".cc-reveal"));

  // Motion is reduced (or the head script never ran): show everything as-is.
  if (!root.classList.contains("cc-motion")) {
    elements.forEach((el) => el.classList.add("is-in"));
    return () => {};
  }

  root.dataset.ccMotionReady = "1";

  if (!("IntersectionObserver" in window)) {
    elements.forEach((el) => el.classList.add("is-in"));
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        observer.unobserve(entry.target);
      });
    },
    // The bottom margin is what sets the trigger line: -25% pulls it a quarter
    // of the way up the screen, so an element starts its 0.7s reveal once it is
    // properly in view instead of finishing while it is still at the bottom
    // edge. Kept off -35% and lower, which would leave the last screenful of a
    // short page unable to reach the line at all.
    { rootMargin: "0px 0px -25% 0px", threshold: 0.1 }
  );

  elements.forEach((el) => observer.observe(el));

  // The trigger line above sits a quarter of the way up the screen, which is
  // right while scrolling and wrong for the very first screen: the hero is
  // bottom-aligned, so its button row lands inside that bottom quarter and never
  // crosses the line until the visitor scrolls. On a 1440x900 window that left
  // "Book a tour" and "See tuition" at opacity 0 on arrival. Anything already on
  // screen when the page loads reveals straight away; everything below the fold
  // keeps the scroll cadence.
  requestAnimationFrame(() => {
    elements.forEach((el) => {
      const box = el.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        el.classList.add("is-in");
        observer.unobserve(el);
      }
    });
  });

  return () => observer.disconnect();
};

// Signature: the book-blue line draws along the 6:30 to 6:30 arc as the
// section scrolls past, reaching each step's sun in turn, and the meadow under
// it comes up after: grass first, then flowers. Decoration only, gated on the
// same motion preference as everything else; the CSS resting state (line drawn
// end to end, grass standing, every flower open) is what shows when this never
// runs.
//
// Two scroll positions, because the meadow sits 360px below the arc's top on a
// desktop and 1024px below on a phone, where the day would be over before the
// meadow was on screen.
//
// The day is the arc's top crossing the viewport: 0 with the top 75% of the
// way down (nothing drawn), 1 with it 20% of the way down (the line at the
// 6:30 pm foot). The start was 85%; the client asked for the day to begin a
// tenth of the screen later, once the section is properly in view. A further
// tenth (65%) was tried on 2026-09-08 and put back the same day: it read as
// the section starting too late.
const SUNRISE_AT = 0.75;
const SUNSET_AT = 0.2;
// The growth is the meadow's top crossing the viewport: 0 with it 95% of the
// way down, the strip just on screen, and 1 with it 50% of the way down. The
// first GRASS_SHARE of that grows the grass, left to right; the flowers open
// over the rest, left to right, so the last blade is up before the first
// flower opens.
const MEADOW_RISE_AT = 0.95;
const MEADOW_BLOOMED_AT = 0.5;
const GRASS_SHARE = 0.35;
// Per-frame glide towards the scroll target, so a flick of the wheel reads as
// travel rather than a jump.
const GLIDE = 0.15;
// The most the growth may move in a second. The glide alone let one flick of
// the wheel carry the strip through its whole range in a quarter of a second
// (measured: grass up in 267ms, the first flower 16ms later), which read as
// grass and flowers appearing together. Capped, that flick still plays the
// grass sweep and then the flowers over 2.5s; a slow scroll stays scrubbed.
const GROWTH_RATE = 0.4;

// Where an element's top sits between two shares of the viewport height, as
// 0 to 1, clamped.
const progress = (top, startAt, endAt) => {
  const start = window.innerHeight * startAt;
  const end = window.innerHeight * endAt;
  return Math.min(1, Math.max(0, (start - top) / (start - end)));
};

const dayArc = () => {
  const band = document.querySelector("[data-cc-day-arc]");
  if (!band || !document.documentElement.classList.contains("cc-motion")) return () => {};

  const fill = band.querySelector("[data-cc-day-arc-fill]");
  const meadow = band.querySelector("[data-cc-day-meadow]");
  // Each sun waits in grey until the line reaches its centre, then comes
  // alive: --cc-sun-at, inline from PagesHelper, is that share of the line's
  // length. Each flower in the meadow opens in turn once the grass is up:
  // --cc-bloom is its share of the width, spread over what the growth has
  // left after the grass.
  const suns = Array.from(band.querySelectorAll("[data-cc-day-sun]")).map((sun) => ({
    sun,
    at: parseFloat(sun.style.getPropertyValue("--cc-sun-at"))
  }));
  const flowers = Array.from(band.querySelectorAll("[data-cc-day-flower]")).map((flower) => ({
    flower,
    opensAt: GRASS_SHARE + parseFloat(flower.style.getPropertyValue("--cc-bloom")) * (1 - GRASS_SHARE)
  }));
  // pathLength="100" on the fill makes the offset a share of the curve. The
  // grass is one write on the meadow: the stylesheet reads --cc-grass and
  // raises each blade across its own slice of it.
  const paint = (day, growth) => {
    fill.style.strokeDashoffset = String(100 - day * 100);
    suns.forEach(({ sun, at }) => sun.classList.toggle("is-lit", day >= at));
    meadow.style.setProperty("--cc-grass", Math.min(1, growth / GRASS_SHARE).toFixed(3));
    flowers.forEach(({ flower, opensAt }) => flower.classList.toggle("is-open", growth >= opensAt));
  };

  const day = { target: 0, current: -1 };
  const growth = { target: 0, current: -1 };
  let frame = 0;
  let lastFrameAt = 0;
  // One glide step towards the target, no further than maxStep; true once the
  // value has settled on it. The first paint lands on the target outright, so
  // a page opened mid-scroll shows the day as it stands.
  const glide = (value, maxStep) => {
    const gap = value.target - value.current;
    if (value.current < 0 || Math.abs(gap) < 0.0005) value.current = value.target;
    else value.current += Math.sign(gap) * Math.min(Math.abs(gap) * GLIDE, maxStep);
    return value.current === value.target;
  };
  const render = (now) => {
    frame = 0;
    // Clamped, because the loop stops between scrolls and the gap since the
    // last frame can be seconds; a resumed loop takes one ordinary step.
    const seconds = Math.min(now - lastFrameAt, 34) / 1000;
    lastFrameAt = now;
    const daySettled = glide(day, Infinity);
    const growthSettled = glide(growth, GROWTH_RATE * seconds);
    paint(day.current, growth.current);
    if (!daySettled || !growthSettled) frame = requestAnimationFrame(render);
  };

  const onScroll = () => {
    day.target = progress(band.getBoundingClientRect().top, SUNRISE_AT, SUNSET_AT);
    growth.target = progress(meadow.getBoundingClientRect().top, MEADOW_RISE_AT, MEADOW_BLOOMED_AT);
    if (!frame) frame = requestAnimationFrame(render);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
  onScroll();

  return () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
    cancelAnimationFrame(frame);
  };
};

// Smooth scroll: Lenis on fine pointers with motion allowed, the gate the
// Klenk showcases in Atlas settled on. It smooths wheel input and writes the
// real window scroll position every frame, so the reveal observer and the day
// arc's scroll listener see ordinary scroll events and need no wiring. Touch
// phones, reduced motion and no-JS keep native scroll. The two settings are
// the Jaffa Group reference values the showcases use. The stylesheet's
// html.lenis rules turn native scroll-behavior off while it runs, or every
// frame it writes would be smoothed a second time.
const smoothScroll = () => {
  if (!window.matchMedia("(prefers-reduced-motion: no-preference) and (hover: hover) and (pointer: fine)").matches) return () => {};

  const lenis = new Lenis({ lerp: 0.1, wheelMultiplier: 0.7, autoRaf: true });
  return () => lenis.destroy();
};

// Every date field on the public site. flatpickr is this site's one date picker,
// so this binds by input type rather than by a data attribute: a date field added
// to any page from here on gets the calendar without anyone remembering to opt in.
// The field still works with this file absent, as a native date input.
const datePickers = () => {
  const inputs = Array.from(document.querySelectorAll("input[type=date]"));
  if (inputs.length === 0) return () => {};

  const instances = inputs.map((input) => attachDatePicker(input));
  return () => instances.forEach((instance) => instance.destroy());
};

// Every phone field on the public site, formatted as the visitor types. Binds by
// input type for the same reason datePickers() does: a telephone field added to
// any page from here on gets the mask without anyone remembering to opt in.
//
// Listens on the document rather than on each field, because the registration
// form's repeating sections add guardian, emergency and pickup rows after this
// has run, and a row added later has to be masked too.
//
// The field submits its formatted text and works untouched with this file
// absent, so nothing downstream depends on the mask having run.
const phoneMasks = () => {
  // A half-typed number is told so through setCustomValidity, which is what the
  // registration form's step gate and the browser's own bubble both read, so
  // neither had to learn anything about phones. Cleared as soon as the tenth
  // digit lands.
  const onInput = (event) => {
    if (!event.target.matches?.("input[type=tel]")) return;
    maskPhoneInput(event.target);
    event.target.setCustomValidity(phoneValidityMessage(event.target.value));
  };

  // Values already on the page: a resumed draft, or a re-render after a failed
  // submission, both of which come back as the parent originally typed them.
  document.querySelectorAll("input[type=tel]").forEach((input) => {
    input.value = formatPhone(input.value);
    input.setCustomValidity(phoneValidityMessage(input.value));
  });

  // Capture phase, so the mask and the validity are settled before the
  // registration form's own input listener, on the form, asks the field whether
  // it passes. In the bubble phase that listener ran first and judged the value
  // from the keystroke before, so the flag under a phone cleared one digit late.
  document.addEventListener("input", onInput, true);
  return () => document.removeEventListener("input", onInput, true);
};

// "Save and finish later" is a submit button that posts a deliberately
// half-filled form to a different endpoint. Neither the step gate below nor the
// "Sending..." relabel further down may treat it as the real submission.
const savesForLater = (event) => Boolean(event.submitter?.hasAttribute("formnovalidate"));

// The registration form paced one step at a time. The server keeps rendering all
// five steps, so with this file absent the page is the long single-screen form
// it has always been; the rail, the Back/Skip/Next buttons and the review panel
// all ship hidden and are unhidden here, so a no-JS visitor never sees a dead
// control.
const registrationSteps = () => {
  const form = document.querySelector("[data-cc-registration-form]");
  if (!form) return () => {};
  const steps = Array.from(form.querySelectorAll("[data-reg-step]"));
  if (steps.length === 0) return () => {};

  // We hide whole steps, and a required field inside a hidden element makes the
  // browser refuse to submit with "An invalid form control is not focusable",
  // reported to the console and nowhere a parent can see it. Gating is ours from
  // here on.
  form.noValidate = true;

  const rail = form.querySelector("[data-reg-rail]");
  const railFill = form.querySelector("[data-reg-rail-fill]");
  const railDots = Array.from(form.querySelectorAll("[data-reg-rail-step]"));
  const railCounter = form.querySelector("[data-reg-rail-counter]");
  const railStepName = form.querySelector("[data-reg-rail-step-name]");
  const reviewPanel = form.querySelector("[data-reg-review-panel]");
  let current = 0;
  // The furthest step the parent has actually reached. A rail dot past it is
  // locked, so the only way forward is Next, which validates the step on screen
  // first. Everything at or behind it stays one click away, which is the point:
  // going back to correct an answer must never cost the parent their place.
  let furthest = 0;

  const isBlank = (control) => {
    if (control.type === "checkbox" || control.type === "radio") return !control.checked;
    return atDefault(control) || control.value.trim() === "";
  };

  // What the parent typed, read back in a line. The shape of each line is
  // decided here rather than in the markup, because it is built from the step's
  // own fields: a control's value by name suffix, a repeating section's names by
  // its legend, a health question's rows or its "none" tick. Everything goes in
  // through textContent, since every word of it is the parent's.
  const valueNamed = (step, suffix) => {
    const control = step.querySelector(`[name$="[${suffix}]"]`);
    if (!control) return "";
    // The display half of a date field, which reads "March 14, 2022".
    return (control._flatpickr?.altInput || control).value.trim();
  };

  const namesBySection = (step) =>
    Array.from(step.querySelectorAll("[data-reg-section]")).map((section) => {
      const legend = section.querySelector("legend")?.textContent.trim() || "";
      const names = Array.from(section.querySelectorAll("[data-reg-row]"))
        .map((row) => row.querySelector('[name$="[name]"]')?.value.trim())
        .filter(Boolean);
      return names.length > 0 ? `${legend}: ${names.join(", ")}` : "";
    }).filter(Boolean);

  // A health question reads as its rows, "none" when its box is ticked, or
  // "given" for a written answer: the answer itself may run to a page.
  const healthAnswers = (step) =>
    Array.from(step.querySelectorAll("fieldset")).map((panel) => {
      const box = panel.querySelector("[data-reg-none-toggle]");
      if (!box) return "";
      const legend = panel.querySelector("legend")?.textContent.trim() || "";
      if (box.checked) return `${legend}: none`;
      const rows = Array.from(panel.querySelectorAll("[data-reg-row]"))
        .map((row) => row.querySelector('[name$="[name]"]')?.value.trim())
        .filter(Boolean);
      if (rows.length > 0) return `${legend}: ${rows.join(", ")}`;
      const written = Array.from(panel.querySelectorAll("textarea")).some((area) => area.value.trim() !== "");
      return written ? `${legend}: given` : "";
    }).filter(Boolean);

  const signedLine = (step, suffix) => {
    const name = valueNamed(step, suffix);
    return name ? `Signed ${name}` : "Not yet signed";
  };

  const summaryOf = (index, step) => {
    switch (index) {
      case 0: {
        const parts = [valueNamed(step, "child_name")];
        const born = valueNamed(step, "child_birth_date");
        const start = valueNamed(step, "requested_start_date");
        if (born) parts.push(`born ${born}`);
        if (start) parts.push(`starting ${start}`);
        return parts.filter(Boolean).join(", ");
      }
      case 1: {
        const town = [valueNamed(step, "city"), valueNamed(step, "state")].filter(Boolean).join(", ");
        return [valueNamed(step, "street_address"), [town, valueNamed(step, "zip")].filter(Boolean).join(" ")]
          .filter(Boolean).join(", ");
      }
      case 2: {
        const lines = namesBySection(step);
        const doctor = valueNamed(step, "physician_name");
        const dentist = valueNamed(step, "dentist_name");
        if (doctor) lines.push(`Doctor: ${doctor}`);
        if (dentist) lines.push(`Dentist: ${dentist}`);
        return lines.join(" · ");
      }
      case 3:
        return [...healthAnswers(step), signedLine(step, "health_attestation_name")].join(" · ");
      default:
        return "";
    }
  };

  // The child's name and start date, carried down to the agreement on the last
  // step: the document there speaks of "the above named child", and the name
  // was four steps back. The server fills the same spans on a re-render.
  const refreshSigningFor = () => {
    const child = form.querySelector("[data-reg-signing-child]");
    const start = form.querySelector("[data-reg-signing-start]");
    const wrap = form.querySelector("[data-reg-signing-start-wrap]");
    if (!child || !start || !wrap) return;

    const name = valueNamed(steps[0], "child_name");
    const date = valueNamed(steps[0], "requested_start_date");
    child.textContent = name || "your child";
    start.textContent = date;
    wrap.hidden = date === "";
  };

  const refreshReview = () => {
    if (!reviewPanel) return;
    refreshSigningFor();

    reviewPanel.querySelectorAll("[data-reg-review-step]").forEach((row) => {
      const status = row.querySelector("[data-reg-review-status]");
      const summary = row.querySelector("[data-reg-review-summary]");
      const index = Number(row.dataset.regReviewStep) - 1;
      const step = steps[index];
      if (!status || !step) return;

      if (summary) summary.textContent = summaryOf(index, step);

      const controls = Array.from(step.querySelectorAll("input, select, textarea")).filter(
        (control) => control.type !== "hidden" && !control.disabled
      );
      // What the step still owes, not how many boxes are empty. Counting every
      // blank reported "9 fields left blank" for a step where nothing at all was
      // required, which reads as a problem while describing a finished form, and
      // gave a parent no way to tell which of the nine mattered. checkValidity is
      // the same test the Next gate applies, so the two cannot drift.
      const needed = controls.filter((control) => !control.checkValidity()).length;
      // A step with nothing required is never "missing" anything, so an untouched
      // one would read as done. Worth saying it is empty, in case the parent meant
      // to fill it and moved past with Skip.
      const untouched = controls.every(isBlank);

      // textContent, never markup: none of this wording comes from the server,
      // but the fields it counts hold what a parent typed.
      if (needed === 1) status.textContent = "1 field still needed";
      else if (needed > 1) status.textContent = `${needed} fields still needed`;
      else if (untouched) status.textContent = "Nothing added";
      else status.textContent = "Nothing missing";
    });
  };

  const showStep = (index) => {
    current = Math.max(0, Math.min(index, steps.length - 1));
    furthest = Math.max(furthest, current);
    if (current === steps.length - 1) refreshReview();

    // The DOM hidden property, never a class and never an inline display rule:
    // the "Add another contact" buttons already reveal this way, and a row
    // cloned into a step hidden by class would still take up space and still be
    // tabbable.
    steps.forEach((step, i) => {
      step.hidden = i !== current;
    });

    // A dot is filled when the parent has reached its step and locked when they
    // have not. The fill tracks how far they have got, not where they are
    // standing, so stepping back to change an answer does not empty the dots of
    // the steps they already finished. The bar below is the one that tracks
    // where they are standing, and the counter under it says so in words.
    railDots.forEach((dot, i) => {
      if (i === current) dot.setAttribute("aria-current", "step");
      else dot.removeAttribute("aria-current");
      dot.classList.toggle("bg-sun", i <= furthest);
      dot.classList.toggle("bg-line", i > furthest);
      dot.classList.toggle("cursor-pointer", i <= furthest);
      dot.classList.toggle("cursor-not-allowed", i > furthest);
      // disabled, not aria-disabled: a locked dot should not take a click and
      // should not sit in the tab order between two steps the parent can open.
      dot.disabled = i > furthest;
    });

    if (railFill) railFill.style.transform = `scaleX(${current / (steps.length - 1)})`;

    if (railCounter) railCounter.textContent = `Step ${current + 1} of ${steps.length}`;

    // The step's own name, taken from the dot's aria-label ("Step 3, Grown-ups")
    // rather than written out a second time here. Shown only below lg, where the
    // labels under the dots are hidden and the rail is five anonymous circles.
    if (railStepName) {
      const label = railDots[current]?.getAttribute("aria-label")?.split(", ")[1];
      railStepName.textContent = label ? ` · ${label}` : "";
    }

    // The heading carries tabindex="-1" for this: without the move, a keyboard
    // visitor is left focused on a Next button that is now hidden.
    steps[current].querySelector("[data-reg-step-heading]")?.focus();

    // Only when the form has scrolled off the top. Advancing out of a short step
    // should not yank the page around.
    if (form.getBoundingClientRect().top < 0) form.scrollIntoView({ block: "start" });
  };

  // A failed Next used to lean on the browser's bubble, which flags one field at
  // a time and vanishes on the next click. These flags mark every field the step
  // still needs at once, in the same ember style the server's re-render uses, and
  // each one clears as soon as the parent fills its field.
  const missedMessages = new WeakMap();

  // The same fields, kept in insertion order for the summary at the top of the
  // form. The value says who flagged the field: a browser flag goes when the field
  // passes its own check, a server flag goes as soon as the parent edits the
  // field, because the server's complaint is about the value that was sent.
  const missed = new Map();

  const SUMMARY_STEP_HEADING = "Check these before you go on";
  const SUMMARY_SEND_HEADING = "Check these before you send";

  const summary = form.querySelector("[data-reg-summary]");
  const summaryList = summary?.querySelector("[data-reg-summary-list]");
  const summaryHeading = summary?.querySelector("[data-reg-summary-heading]");
  // Rules that belong to no field, kept from the server's render so rewriting the
  // list does not throw them away. A fresh submission replaces them.
  let baseMessages = summary
    ? Array.from(summary.querySelectorAll("[data-reg-summary-base]")).map((item) => item.textContent.trim())
    : [];
  let summaryHeadingText = summaryHeading?.textContent.trim() || "";

  const stepNameOf = (step) =>
    railDots[steps.indexOf(step)]?.getAttribute("aria-label")?.split(", ")[1] || "";

  // flatpickr hides the real input and puts an unnamed display input beside it.
  // The flag sits on the display input, but the label and the id belong to the
  // real one, so the summary reads and links through the pair.
  const labelledControl = (control) => control._flatpickr?.input || control;

  // "Home: ZIP code", or "Grown-ups: Emergency contacts 2, Full name". The section
  // and row number are only added inside a repeating section, which is the one
  // place a label like "Full name" appears more than once.
  //
  // A health question's "none" box is the exception: its label is a whole
  // sentence, and "Known allergies and food sensitivities, My child has no known
  // allergies or food sensitivities" read as one run-on. The question's legend and
  // what is owed to it is the whole message.
  const summaryLabel = (input) => {
    const control = labelledControl(input);
    const step = stepNameOf(control.closest("[data-reg-step]"));
    if (control.hasAttribute("data-reg-none-toggle")) {
      const legend = control.closest("fieldset")?.querySelector("legend")?.textContent.trim() || "";
      return `${step}: ${legend}, answer or tick the box`;
    }

    const field = (control.labels?.[0]?.textContent || control.name).replace("*", "").trim();
    const section = control.closest("[data-reg-section]");
    if (!section) return `${step}: ${field}`;

    const legend = section.querySelector("legend")?.textContent.trim() || "";
    const rows = Array.from(section.querySelectorAll("[data-reg-row]"));
    const row = control.closest("[data-reg-row]");
    const which = rows.length > 1 && row ? ` ${rows.indexOf(row) + 1}` : "";
    return `${step}: ${legend}${which}, ${field}`;
  };

  const summaryItem = (control) => {
    const item = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.regSummaryTarget = labelledControl(control).id;
    button.className = "text-left underline underline-offset-4 hover:no-underline cursor-pointer";
    // textContent, never markup: these strings come from the page's own labels.
    button.textContent = summaryLabel(control);
    item.append(button);
    return item;
  };

  const baseItem = (message) => {
    const item = document.createElement("li");
    item.dataset.regSummaryBase = "";
    item.textContent = message;
    return item;
  };

  const renderSummary = (headingText) => {
    if (!summary || !summaryList || !summaryHeading) return;
    if (headingText) summaryHeadingText = headingText;

    if (missed.size === 0 && baseMessages.length === 0) {
      summary.hidden = true;
      summaryList.replaceChildren();
      return;
    }

    summaryHeading.textContent = summaryHeadingText;
    summaryList.replaceChildren(
      ...baseMessages.map(baseItem),
      ...Array.from(missed.keys()).map(summaryItem)
    );
    summary.hidden = false;
  };

  // What to say under a field the step gate has flagged. The browser's own string
  // is right for an empty required field ("Please fill out this field.") and useless
  // for a `pattern`, where every browser says "Please match the requested format."
  // and none of them will say which format: the `title` attribute does not reach
  // validationMessage. A field with a pattern therefore carries its own sentence in
  // data-cc-invalid-message, and it is used only for the mismatch it describes, so
  // a blank field still gets the browser's clearer wording.
  const missedMessageFor = (control) =>
    (control.validity.patternMismatch && control.dataset.ccInvalidMessage) || control.validationMessage;

  const flagMissed = (control) => {
    missed.set(control, "browser");
    control.setAttribute("aria-invalid", "true");
    let message = missedMessages.get(control);
    if (!message) {
      message = document.createElement("p");
      message.className = "mt-1.5 text-sm text-ember-deep";
      if (control.id) {
        message.id = `${control.id}-missed`;
        control.setAttribute("aria-describedby", message.id);
      }
      // The agreement checkbox sits in a flex row with its label; its message goes
      // after that row, where the server puts its own. Everything else gets the
      // message inside the field's wrapper, under the input.
      const wrapper = control.closest("div");
      if (control.type === "checkbox") wrapper?.insertAdjacentElement("afterend", message);
      else wrapper?.append(message);
      missedMessages.set(control, message);
    }
    // textContent, never markup: this string comes from the browser or from the
    // field's own markup, never from the server.
    message.textContent = missedMessageFor(control);
  };

  const clearMissed = (control) => {
    missed.delete(control);
    const message = missedMessages.get(control);
    if (!message) return;

    missedMessages.delete(control);
    message.remove();
    control.removeAttribute("aria-invalid");
    // Only our own pointer. A field the server flagged keeps its aria-describedby.
    if (control.getAttribute("aria-describedby") === message.id) control.removeAttribute("aria-describedby");
  };

  // Flags what the step still needs and unflags what it no longer does, without
  // moving focus or touching the summary: the submit path runs this over every
  // step before deciding where to send the parent.
  const flagStep = (step) => {
    const controls = Array.from(step.querySelectorAll("input, select, textarea")).filter(
      (control) => control.type !== "hidden" && !control.disabled
    );
    const missedHere = controls.filter((control) => !control.checkValidity());

    controls.forEach((control) => {
      if (!missedHere.includes(control)) clearMissed(control);
    });
    missedHere.forEach(flagMissed);
    return missedHere;
  };

  // The summary first, then the field. Focusing the field alone scrolled it into
  // view and left the summary, and its heading, under the sticky header, so the
  // parent saw a flag with no list above it. The summary carries scroll-margin
  // for the header; the field is focused without a scroll of its own, since on a
  // long step it may sit a screen below and the summary's link will take the
  // parent there.
  const showMissed = (first) => {
    if (summary && !summary.hidden) summary.scrollIntoView({ block: "start" });
    first?.focus({ preventScroll: true });
  };

  const stepIsValid = (step) => {
    const missedHere = flagStep(step);
    renderSummary(SUMMARY_STEP_HEADING);
    showMissed(missedHere[0]);
    return missedHere.length === 0;
  };

  const onClick = (event) => {
    const target = event.target;
    if (target.closest("[data-reg-back]")) return showStep(current - 1);
    if (target.closest("[data-reg-next]")) {
      if (stepIsValid(steps[current])) showStep(current + 1);
      return;
    }

    // An item in the summary opens the step its field lives on and puts the
    // cursor in it, which is the whole point of listing the field by name.
    const jump = target.closest("[data-reg-summary-target]");
    if (jump) {
      const control = document.getElementById(jump.dataset.regSummaryTarget);
      if (!control) return;
      showStep(steps.indexOf(control.closest("[data-reg-step]")));
      // The display input, not the hidden real one flatpickr leaves behind: a
      // hidden control cannot take focus.
      (control._flatpickr?.altInput || control).focus();
      return;
    }

    // A rail dot and a review Edit button both jump without validating, and both
    // can only reach a step the parent has already been sent to. Going forward is
    // Next's job, and Next validates first.
    const dot = target.closest("[data-reg-rail-step]");
    if (dot) {
      const index = Number(dot.dataset.regRailStep) - 1;
      if (index <= furthest) showStep(index);
      return;
    }

    const edit = target.closest("[data-reg-review-edit]");
    if (edit) showStep(Number(edit.dataset.regReviewEdit) - 1);
  };

  const onSubmit = (event) => {
    if (savesForLater(event)) return;
    if (form.checkValidity()) return;

    // Cancel the submit and stop the rest of the listeners on this form:
    // registrationForm()'s handler is registered after this one and would
    // otherwise disable the button and relabel it for a submission that never
    // left.
    event.preventDefault();
    event.stopImmediatePropagation();

    // Every step, not just the one on screen: a parent pressing Send has finished
    // asking about one step at a time and wants the whole list. The server's own
    // rules were answered by this attempt, so its messages go.
    baseMessages = [];
    const missedByStep = steps.map(flagStep);
    renderSummary(SUMMARY_SEND_HEADING);

    const firstStep = missedByStep.findIndex((list) => list.length > 0);
    if (firstStep < 0) return;

    showStep(firstStep);
    showMissed(missedByStep[firstStep][0]);
  };

  // "input" covers typing; "change" covers the selects and the flatpickr
  // calendars, which set a value without a keystroke. A calendar pick fires its
  // change on the hidden real input, but the flag sits on the visible display
  // input flatpickr made, so the event is mapped across the pair.
  const onFill = (event) => {
    const control = event.target._flatpickr?.altInput || event.target;
    // A server flag goes on the first keystroke: the server judged the value that
    // was sent, and the parent is typing a different one. A browser flag waits
    // until the field actually passes.
    const flaggedBy = missed.get(control);
    if (flaggedBy === "server" || (flaggedBy === "browser" && control.checkValidity())) {
      clearMissed(control);
      renderSummary();
    }
  };

  if (rail) rail.hidden = false;
  if (reviewPanel) reviewPanel.hidden = false;
  form.querySelectorAll("[data-reg-back], [data-reg-next]").forEach((button) => {
    button.hidden = false;
  });

  // The server re-renders the whole form when it rejects it, flagging the fields
  // that failed. Opening on step 1 would leave a parent hunting for what went
  // wrong, so start on the step holding the first flagged field.
  //
  // Not every rejection flags a field. A model rule about the record as a whole,
  // "add at least one parent or guardian", has no attribute to attach to, so
  // nothing carries aria-invalid and the parent would land on step 1 reading an
  // alert about step 3. Falling back to the first unfilled required control puts
  // them where they can act, whatever the rule was.
  const flagged = form.querySelector("[aria-invalid=true]") ||
    Array.from(form.querySelectorAll("[required]")).find((control) => !control.checkValidity());
  const opening = flagged ? steps.findIndex((step) => step.contains(flagged)) : 0;

  // A rejected submission is a return trip, not a first visit. `furthest` resets
  // with the page, so the rail would decide a parent bounced back to step 3 had
  // never been past it and lock steps 4 and 5, leaving them to press Next through
  // steps they have not touched before they could reach Send again, with the
  // review panel out of sight the whole way.
  //
  // The unlock is sound rather than generous: the server only renders this
  // summary visible when it rejected a submission, and a submission can only come
  // from step 5, so the parent has demonstrably been through every step and the
  // form still holds their answers. The forward-only rule exists to stop somebody
  // skipping a step they have never filled, which is not this.
  if (summary && !summary.hidden) furthest = steps.length - 1;

  showStep(opening === -1 ? 0 : opening);

  // The fields the server flagged become summary items too, so every one of them
  // is a button that opens its step, and the plain list the server rendered is
  // replaced by the same list a failed Send would write.
  form.querySelectorAll("[aria-invalid=true]").forEach((control) => {
    if (control.matches("input, select, textarea")) missed.set(control, "server");
  });
  if (missed.size > 0) renderSummary();

  // The saved browser copy reopens the step the parent left on, which sits ahead
  // of the one dot a fresh page unlocks. The rail cannot make that jump by
  // design, so the restore asks for it by name: the parent reached that step on
  // the visit before this one, and the lock moves forward with them.
  const onOpenStep = (event) => showStep(Number(event.detail?.step || 1) - 1);

  form.addEventListener("click", onClick);
  form.addEventListener("submit", onSubmit);
  form.addEventListener("input", onFill);
  form.addEventListener("change", onFill);
  form.addEventListener("cc:reg-open-step", onOpenStep);

  return () => {
    form.removeEventListener("click", onClick);
    form.removeEventListener("submit", onSubmit);
    form.removeEventListener("input", onFill);
    form.removeEventListener("change", onFill);
    form.removeEventListener("cc:reg-open-step", onOpenStep);
  };
};

// A select the form opens on a value, marked data-reg-default: the state selects
// open on UT because nearly every family lives there. A select still at that
// value is not an answer the parent gave, so everything that asks whether a row
// or a form has been touched reads it as blank; otherwise a cloned guardian row
// would wake its requirements the moment it appeared, and an untouched form
// would write a browser copy holding nothing but "UT".
const atDefault = (control) =>
  control.tagName === "SELECT" && "regDefault" in control.dataset && control.value === control.dataset.regDefault;

// Gives the default to every blank select in scope that carries one. Run when a
// parent starts an added row or opens a doctor block, never on the cloned
// template: a value there would make an abandoned row look filled in to the
// server, which drops only rows that are blank throughout.
const applyDefaults = (scope) => {
  scope.querySelectorAll("select[data-reg-default]").forEach((select) => {
    if (select.value === "") select.value = select.dataset.regDefault;
  });
};

// Whether a parent has started this row. Checkboxes do not count: ticking "Does not
// work outside the home" is not the parent naming a person, and counting it would
// make a row a parent ticked and abandoned look started.
const rowIsStarted = (row) =>
  Array.from(row.querySelectorAll("input:not([type=hidden]), select, textarea"))
    .some((control) => control.type !== "checkbox" && control.type !== "radio" &&
                       !atDefault(control) && control.value.trim() !== "");

// Keeps one row's `required` flags, and a guardian's workplace block, matching what
// the server will ask of it. Three conditions decide each field:
//
//   in use  - the row is one the model insists on (the first guardian, the first
//             emergency contact, the first out-of-area contact), or the parent has
//             typed something in it. A row a parent added ships with no `required`
//             at all so an unused one cannot trap them, but the server applies the
//             full rule to any row that is not blank, so the flags have to wake with
//             the row. A name typed into a second guardian row used to clear every
//             step and fail at Send with eight errors at once.
//   answered- a row inside a "none" block whose box is ticked is asked for nothing.
//             The parent has said there are no allergies; a half-typed row behind
//             that tick must not hold the step gate on a field nobody can see.
//   at work - the workplace six are asked only while the "Does not work outside the
//             home" box is unticked, which is the same second condition the model's
//             validation applies.
//
// Closing a block drops `required` as well as hiding it, because a required field
// inside a hidden element still fails checkValidity() and the step gate would flag
// fields the parent cannot see.
const syncRow = (row) => {
  const fields = (row.dataset.regRequiredFields || "").split(" ").filter(Boolean);
  if (fields.length === 0) return;

  // data-reg-row-required, not data-reg-row-fixed: the second marks a row its section
  // always keeps, and a health row carries it to keep its Remove button away without
  // being asked for a name it does not have yet.
  const hiddenByBox = row.closest("[data-reg-none-block]")?.hidden === true;
  const inUse = !hiddenByBox && (row.hasAttribute("data-reg-row-required") || rowIsStarted(row));
  const box = row.querySelector("[data-reg-employer-toggle]");
  const atWork = !box || !box.checked;

  const block = row.querySelector("[data-reg-employer-block]");
  if (block) block.hidden = !atWork;

  // A row the parent has started gets the same start the shipped row had.
  if (inUse) applyDefaults(row);

  fields.forEach((field) => {
    // Scoped by name rather than by id: a row cloned after load carries a fresh
    // timestamp in its ids, and the name suffix is what stays stable.
    const control = row.querySelector(`[name$="[${field}]"]`);
    if (!control) return;

    control.required = inUse && (atWork || !block?.contains(control));
  });
};

// Unhides a row's Remove button, unless the row is one its section always keeps.
// Those are the first rows of the sections the form ships open: the first guardian,
// the first emergency contact and the first out-of-area contact, where removing it
// makes the form unsendable and says nothing about it until the server answers, and
// the first allergy, condition and medication row, where removing it would leave a
// section with nothing in it. Every row a parent adds keeps its button.
//
// The marker, not "does this row hold a required field": syncRow flags a row as
// soon as the parent starts it, and reading the flags here would take Remove away
// at exactly the moment a parent who began a row by mistake wants it.
const showRemove = (row) => {
  if (row.hasAttribute("data-reg-row-fixed")) return;

  row.querySelectorAll("[data-reg-remove]").forEach((button) => {
    button.hidden = false;
  });
};

// The registration form's repeating sections. Every section already renders blank
// rows server-side, so the form works with this file absent; all this adds is the
// ability to ask for more rows than were rendered. Add and Remove ship hidden in
// the markup and are unhidden here, so a no-JS visitor never sees a dead button.
const registrationForm = () => {
  // Two forms carry the health questions: the registration itself and the yearly
  // review page a family reaches from the emailed link. Only this script serves both.
  // The steps and the autosave stay on the registration form: the review page is one
  // screen, and a copy of it saved under the registration's key would come back as
  // somebody's registration.
  const form = document.querySelector("[data-cc-registration-form], [data-cc-health-renewal-form]");
  if (!form) return () => {};

  const teardowns = [];

  // Delegated from the form, because a row cloned after load has to be watched
  // too. Every keystroke and every tick re-reads the one row it happened in: a
  // row wakes its requirements when the parent starts it and drops them again if
  // they empty it, and the workplace box opens and closes its block.
  //
  // Both events, because a click fires change while the restored browser copy
  // sets values by hand and announces them with input. syncRow only ever writes
  // what it computes, so running it twice on one click costs nothing.
  // The boxes run first: they own each block's `hidden`, and a row reads that to
  // decide whether it is being asked for anything at all.
  const onRowInput = (event) => {
    syncNoneBoxes();
    const row = event.target.closest?.("[data-reg-row]");
    if (row) syncRow(row);
  };

  // The health step's escapes: a "none" box under each of its five questions ("My
  // child has no known allergies or food sensitivities", and so on). Ticking one
  // hides the question it stands in for, and only that one, so a child with a peanut
  // allergy and nothing else ticks four and answers one. What the parent owes each
  // question is one or the other, an answer in its block or its tick, and while
  // neither is given the box itself is made invalid. The step gate and the summary
  // then flag it like any other field, with no rule of their own, and the model
  // applies the same test.
  //
  // A row inside a block does carry required fields now, so hiding a block re-syncs
  // the rows in it: a half-typed allergy behind a ticked box must not hold the step
  // gate on a field nobody can see.
  //
  // The step script clears a flag when the flagged field itself reports input and
  // passes. A parent clears this one by typing somewhere else, so the box announces
  // the change on their behalf, and only when it changed: this runs from the same
  // listener that event reaches, and would otherwise re-enter itself without end.
  const syncNoneBoxes = () => {
    form.querySelectorAll("[data-reg-none-toggle]").forEach((box) => {
      const block = box.closest("fieldset")?.querySelector("[data-reg-none-block]");
      if (!block) return;

      block.hidden = box.checked;
      block.querySelectorAll("[data-reg-row]").forEach(syncRow);

      const answered =
        Array.from(block.querySelectorAll("[data-reg-row]")).some(rowIsStarted) ||
        Array.from(block.querySelectorAll("textarea")).some((area) => area.value.trim() !== "");
      const before = box.validationMessage;
      box.setCustomValidity(
        box.checked || answered ? "" : "Answer this below, or tick this box if there is nothing to report."
      );
      if (box.validationMessage !== before) box.dispatchEvent(new Event("input", { bubbles: true }));
    });
  };

  // The pass over the rows already on the page, which is what brings a resumed
  // draft and a rejected submission back in the right state. The boxes go first,
  // for the reason onRowInput runs them first.
  syncNoneBoxes();
  form.querySelectorAll("[data-reg-row]").forEach(syncRow);
  form.addEventListener("change", onRowInput);
  form.addEventListener("input", onRowInput);
  teardowns.push(() => form.removeEventListener("change", onRowInput));
  teardowns.push(() => form.removeEventListener("input", onRowInput));

  form.querySelectorAll("[data-reg-section]").forEach((section) => {
    const template = section.querySelector("[data-reg-template]");
    const rows = section.querySelector("[data-reg-rows]");
    const addButton = section.querySelector("[data-reg-add]");
    // A section without all three parts has nothing to clone into, so it keeps
    // its Remove button hidden and is left alone.
    if (!template || !rows || !addButton) return;

    addButton.hidden = false;
    rows.querySelectorAll("[data-reg-row]").forEach(showRemove);

    const onAdd = () => {
      // Date.now() rather than a counter: the index only has to be unique within
      // this submission, and a counter would collide with the rendered rows.
      const markup = template.innerHTML.replaceAll("NEW_INDEX", Date.now().toString());
      const row = document.createElement("div");
      row.innerHTML = markup;
      const added = row.firstElementChild;
      if (!added) return;

      rows.appendChild(added);
      // showRemove first: an added row is empty, so syncRow leaves it with no
      // required field, which is what keeps its Remove button.
      showRemove(added);
      syncRow(added);
      added.querySelector("input:not([type=hidden]), select, textarea")?.focus();
    };

    // A row already on file (the yearly review page renders those) is not dropped
    // from the document, because a row the browser does not post is a row the
    // server leaves alone. Its destroy flag is set, its answers are cleared so
    // syncRow drops the `required` it would otherwise hold on a field nobody can
    // reach, and the row is hidden; the server then destroys it. A row added on this
    // page has no flag and is simply removed, as on the registration form.
    const onRemove = (event) => {
      const button = event.target.closest("[data-reg-remove]");
      if (!button) return;
      const row = button.closest("[data-reg-row]");
      if (!row) return;
      const destroy = row.querySelector("[data-reg-destroy]");
      if (!destroy) return row.remove();

      destroy.value = "1";
      row.querySelectorAll("input:not([type=hidden]), select, textarea").forEach((control) => {
        control.value = "";
      });
      syncRow(row);
      row.hidden = true;
    };

    addButton.addEventListener("click", onAdd);
    rows.addEventListener("click", onRemove);
    teardowns.push(() => addButton.removeEventListener("click", onAdd));
    teardowns.push(() => rows.removeEventListener("click", onRemove));
  });

  const onSubmit = (event) => {
    if (savesForLater(event)) return;
    const submit = form.querySelector("[data-reg-submit]");
    if (!submit) return;
    submit.disabled = true;
    submit.value = "Sending...";
  };

  form.addEventListener("submit", onSubmit);
  teardowns.push(() => form.removeEventListener("submit", onSubmit));

  return () => teardowns.forEach((fn) => fn());
};

// Until this existed, a refresh, a stray Back or a closed tab lost every answer
// on a 67 field form: the only persistence was the "Save and finish later" panel,
// which asks for an email address and sends a link. That still exists and is what
// moves a half-filled form between devices. This is the other half: a copy kept in
// this browser as the parent types, put back on the next visit, on the step they
// left.
//
// One thing is deliberately never written: the agreement checkbox, because agreeing
// is an act at the moment of sending, and a page reload must not perform it on the
// parent's behalf. The emailed draft leaves the same box out
// (RegistrationDraftsController#draft_payload). The typed signature names are kept:
// a name is what the parent wrote.
// The version is bumped whenever the form's fields move, because values are stored by
// position, not by name: an older copy would put each answer in the box that now sits
// where it used to. v2 came with the reversed workplace box, v3 with the out-of-area
// address, the doctor and dentist blocks and the special-needs box, v4 with the
// optional address on every emergency and pickup row. v4's four new inputs land on the
// row the form ships, so every stored copy is one short and restore would drop it
// anyway; bumping makes that the documented outcome rather than a silent one. v5 came
// with the three signed documents merging into one: three check boxes and two name
// fields went, so every input after them moved, and a v4 copy restored into this form
// would scatter its answers rather than drop them.
const AUTOSAVE_KEY = "cc-registration-v5";
const AGREEMENT_BOXES = ["[agreement]"];
// Matches RegistrationDraft::EXPIRY, so the two ways of coming back go stale together.
const AUTOSAVE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

// A date field is the awkward one. flatpickr turns the real, named input into a
// hidden one and puts an unnamed display input beside it, so selecting on
// :not([type=hidden]) skips the value and selecting on a name skips the display.
// The real input is taken by its _flatpickr property, which the library attaches
// to every element it manages, and put back through the instance so the visible
// half updates with it.
// Whether a repeating row holds anything a parent typed or chose. The hidden kind
// field every row carries is skipped, or every row would count as answered.
const rowHasAnAnswer = (row) =>
  Array.from(row.querySelectorAll("input:not([type=hidden]), select, textarea"))
    .some((control) => (control.type === "checkbox" || control.type === "radio"
      ? control.checked
      : !atDefault(control) && control.value !== ""));

const autosaveFields = (form) =>
  Array.from(form.querySelectorAll("input, select, textarea"))
    .filter((control) => control.name &&
                         control.type !== "submit" &&
                         (control.type !== "hidden" || control._flatpickr) &&
                         !AGREEMENT_BOXES.some((box) => control.name.endsWith(box)) &&
                         !control.closest("[aria-hidden=true]"));

const registrationAutosave = () => {
  const form = document.querySelector("[data-cc-registration-form]");
  if (!form) return () => {};

  let timer = null;
  // Set once a real submission is on its way out. pagehide fires after submit, so
  // without this the flush below would write the copy straight back after the
  // submit handler had just dropped it, and the thank-you page would still be
  // carrying the family's answers.
  let submitting = false;

  // Every localStorage call is wrapped: it throws in private browsing and when
  // the quota is full, and a form that will not resume is much better than a
  // form that will not type.
  const clear = () => {
    try { window.localStorage.removeItem(AUTOSAVE_KEY); } catch { /* nothing to undo */ }
  };

  // Copies written under an older key were never read again and never removed,
  // so a browser that filled the form in August still carried the v1 answers.
  // Swept once here; the key list grows by one each time AUTOSAVE_KEY moves.
  ["cc-registration-v1", "cc-registration-v2", "cc-registration-v3", "cc-registration-v4"].forEach((key) => {
    try { window.localStorage.removeItem(key); } catch { /* blocked: nothing to sweep */ }
  });

  const read = () => {
    try {
      const raw = window.localStorage.getItem(AUTOSAVE_KEY);
      if (!raw) return null;
      const saved = JSON.parse(raw);
      if (!saved || Date.now() - saved.savedAt > AUTOSAVE_MAX_AGE_MS) return null;
      return saved;
    } catch {
      return null;
    }
  };

  const valueOf = (control) =>
    control.type === "checkbox" || control.type === "radio" ? control.checked : control.value;

  // Whether a control still holds what the server rendered. Checkboxes are
  // compared against defaultChecked rather than against false, so a box the server
  // ships ticked stays untouched until the parent touches it. A resumed draft ships
  // several such boxes, and measuring them against false would make the form look
  // answered before anybody typed.
  // A select is measured the same way, against the option the server shipped
  // selected, so a state select opening on UT does not make a blank form look
  // answered; a text field is measured against empty, so a server re-render
  // after a rejected submission still counts as something worth keeping.
  const isUntouched = (control, value) => {
    if (control.type === "checkbox" || control.type === "radio") return value === control.defaultChecked;
    if (control.tagName === "SELECT") {
      return value === (Array.from(control.options).find((option) => option.defaultSelected)?.value ?? "");
    }
    return value === "";
  };

  const save = () => {
    if (submitting) return;

    const fields = autosaveFields(form);
    const values = fields.map(valueOf);
    // An untouched form writes nothing, which is also how the copy is dropped
    // after a successful submission: that page renders an empty form.
    if (fields.every((control, i) => isUntouched(control, values[i]))) return clear();

    try {
      window.localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({
        savedAt: Date.now(),
        step: Number(form.querySelector("[data-reg-step]:not([hidden])")?.dataset.regStep) || 1,
        rows: Array.from(form.querySelectorAll("[data-reg-section]"))
          .map((section) => section.querySelectorAll("[data-reg-row]").length),
        values
      }));
    } catch { /* full or blocked: the form still works, it just will not resume */ }
  };

  const restore = (saved) => {
    // Grow each repeating section back to the number of rows the parent had
    // before the values go in, or every value after the first added row lands in
    // the wrong field.
    const sections = Array.from(form.querySelectorAll("[data-reg-section]"));
    if (!Array.isArray(saved.rows) || saved.rows.length !== sections.length) return;

    // How many rows the server shipped, read before the growth below. A copy
    // saved when a section still opened with a blank row would otherwise reopen
    // that row on a form that no longer ships one.
    const shipped = sections.map((section) => section.querySelectorAll("[data-reg-row]").length);

    sections.forEach((section, i) => {
      const add = section.querySelector("[data-reg-add]");
      if (!add) return;
      while (section.querySelectorAll("[data-reg-row]").length < saved.rows[i]) add.click();
    });

    // Matched by position, not by name: a row added a moment ago carries a fresh
    // timestamp in its field names, so the names would not line up. The length
    // check is the guard, and it is what makes a form whose shape changed between
    // visits drop the old copy instead of scattering it across the wrong fields.
    const fields = autosaveFields(form);
    if (Array.isArray(saved.values) && fields.length === saved.values.length) {
      fields.forEach((control, i) => {
        const value = saved.values[i];
        if (control._flatpickr) {
          // Through the instance, not the value: setting the hidden input directly
          // leaves the visible display input showing nothing.
          control._flatpickr.setDate(value, false);
          return;
        }
        if (control.type === "checkbox" || control.type === "radio") control.checked = Boolean(value);
        else control.value = value;
        // Lets the phone mask format what was restored, so a resumed form reads
        // exactly like the one the parent left.
        control.dispatchEvent(new Event("input", { bubbles: true }));
      });
    }

    // Every row the copy asked for that came back with nothing in it goes again.
    // Only the rows the server shipped are kept while empty, so a parent whose
    // saved copy predates a shape change sees today's form, not the old one.
    sections.forEach((section, i) => {
      Array.from(section.querySelectorAll("[data-reg-row]")).slice(shipped[i]).forEach((row) => {
        if (!rowHasAnAnswer(row)) row.remove();
      });
    });

    // Not the rail dot: the rail only opens a step the parent has already reached
    // in this visit, and this copy is from the last one. The step script listens
    // for this event and unlocks the rail up to the step it names.
    form.dispatchEvent(new CustomEvent("cc:reg-open-step", { detail: { step: saved.step } }));
  };

  const queueSave = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(save, 300);
  };

  // Writing on every keystroke of a 67 field form is wasteful, so saves are
  // debounced. That leaves a 300ms hole: a parent who types and immediately
  // reloads, or closes the tab, loses the last field they touched. This closes it
  // by writing straight away when the page goes.
  //
  // pagehide rather than beforeunload, and visibilitychange alongside it, because
  // a phone backgrounding a tab may never fire an unload event at all.
  const flush = () => {
    window.clearTimeout(timer);
    save();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") flush();
  };

  const onSubmit = (event) => {
    if (savesForLater(event)) return;
    // Cancel first. A queued save firing after the clear would put the copy
    // straight back and leave it there through the thank-you page.
    submitting = true;
    window.clearTimeout(timer);
    clear();
  };

  // A resumed draft, or a rejected submission of one, arrives with the answers the
  // server put in it, and those win. Restoring over them silently replaced a
  // resumed draft with whatever this browser happened to be holding: a family
  // registering a second child from the same computer opened the emailed link for
  // the first one and got the second one's answers, so sending filed the wrong child
  // and destroyed the right one's draft. The save() below then writes what the server
  // rendered over the stale copy, which is what makes this a repair rather than a skip.
  //
  // The server says so on the form. This used to read the presence of a hidden
  // draft_token field, which stopped existing when the token moved into the session:
  // the token was the family's credential and a hidden field published it in the page
  // source. A flag carries the same fact and no secret.
  const resumingADraft = form.hasAttribute("data-cc-resuming-draft");

  const saved = resumingADraft ? null : read();
  if (saved) restore(saved);
  // Captures a server re-render after a rejected submission, which arrives with
  // the parent's answers already in it and no input event to notice them.
  save();

  form.addEventListener("input", queueSave);
  form.addEventListener("change", queueSave);
  form.addEventListener("click", queueSave);
  form.addEventListener("submit", onSubmit);
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    window.clearTimeout(timer);
    form.removeEventListener("input", queueSave);
    form.removeEventListener("change", queueSave);
    form.removeEventListener("click", queueSave);
    form.removeEventListener("submit", onSubmit);
    window.removeEventListener("pagehide", flush);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};

const start = () => {
  cleanup?.();
  // registrationSteps() before registrationForm(): both listen for submit on the
  // same form, listeners fire in the order they were registered, and the step
  // gate has to run first so a cancelled submit never reaches the handler that
  // disables the button.
  // registrationAutosave() last: restoring clicks the Add buttons that
  // registrationForm() wires up and fires the open-step event that
  // registrationSteps() listens for, so both have to be listening before it runs.
  const teardowns = [ mobileMenu(), smoothScroll(), reveals(), dayArc(), datePickers(), phoneMasks(),
                      registrationSteps(), registrationForm(), registrationAutosave() ];
  cleanup = () => teardowns.forEach((fn) => fn());
};

// This bundle is deliberately standalone: the marketing layout does NOT load
// application.js, because a seven page brochure site has no business shipping
// the 2.4MB dashboard bundle (ApexCharts, Tom Select, Flatpickr, Stimulus).
// That means Turbo isn't present and turbo:load never fires here, so
// DOMContentLoaded is the real signal. turbo:load is kept in case this layout
// ever adopts Turbo; start() tears down first, so a double fire is harmless.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
document.addEventListener("turbo:load", start);
